// src/pages/analytics/payables/apShared.tsx
//
// Small pieces shared by the A/P + purchasing tabs (agent 3.24.0+).

import type { ReactNode } from 'react';
import { AlertTriangle, Download, FileSpreadsheet, RefreshCw } from 'lucide-react';

export const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
export const fmtCur2 = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
export const fmtCost = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(v);
export const fmtNum = (v: number) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(v);

export function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function isoYearStart(): string {
  return `${new Date().getFullYear()}-01-01`;
}

export function needsNewerAgent(err: unknown): boolean {
  return /404|unknown recipe|not found/i.test((err as Error)?.message ?? '');
}

export const pill = (on: boolean) => `px-2 py-1.5 text-[11px] rounded-md border transition-all ${
  on ? 'bg-blue-500/15 text-blue-200 border-blue-500/40'
     : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'}`;

export const selectCls = 'px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50';

export const ExportButtons = ({ onCsv, onXlsx, disabled, what }: { onCsv: () => void; onXlsx: () => void; disabled: boolean; what: string }) => (
  <>
    <button onClick={onCsv} disabled={disabled} title={`Export ${what} to CSV`}
      className="px-2 py-1 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
      <Download size={11} /> CSV
    </button>
    <button onClick={onXlsx} disabled={disabled} title={`Export ${what} to Excel`}
      className="px-2 py-1 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
      <FileSpreadsheet size={11} /> Excel
    </button>
  </>
);

export function LoadError({ error, what, minAgent = '3.24.0' }: { error: unknown; what: string; minAgent?: string }) {
  if (needsNewerAgent(error)) {
    return (
      <div className="p-4 rounded-md bg-slate-800/40 border border-slate-700/40 text-slate-300 text-sm flex items-center gap-2">
        <AlertTriangle size={16} className="text-amber-400" /> Needs agent {minAgent}+ — update the agent to see {what}.
      </div>
    );
  }
  return (
    <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
      <AlertTriangle size={16} />
      <div>
        <div className="font-medium">Couldn't load {what}</div>
        <div className="text-xs text-red-300/70 mt-0.5">{(error as Error)?.message}</div>
      </div>
    </div>
  );
}

export const Spinner = () => (
  <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
);

export function Tile({ label, value, sub, tone = 'slate' }: { label: string; value: ReactNode; sub?: ReactNode; tone?: 'slate' | 'violet' | 'red' | 'amber' | 'emerald' }) {
  const border = {
    slate: 'border-slate-700/40 bg-slate-900/40',
    violet: 'border-violet-500/30 bg-violet-500/5',
    red: 'border-red-500/30 bg-red-500/5',
    amber: 'border-amber-500/30 bg-amber-500/5',
    emerald: 'border-emerald-500/30 bg-emerald-500/5',
  }[tone];
  return (
    <div className={`rounded-xl border p-4 ${border}`}>
      <div className="text-xs text-slate-400 uppercase tracking-wider">{label}</div>
      <div className="text-xl font-semibold text-white mt-1 tabular-nums">{value}</div>
      {sub != null && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export function ChangePct({ pct, invert = false }: { pct: number | null | undefined; invert?: boolean }) {
  if (pct == null) return <span className="text-slate-600">–</span>;
  // For costs a rise is bad (invert = true → up is red).
  const good = invert ? pct < 0 : pct > 0;
  const cls = Math.abs(pct) < 0.05 ? 'text-slate-400' : good ? 'text-emerald-400' : 'text-red-400';
  return <span className={`tabular-nums ${cls}`}>{pct > 0 ? '+' : ''}{pct.toFixed(1)}%</span>;
}

export const thCls = 'px-3 py-2 text-left text-[11px] font-medium text-slate-400 uppercase tracking-wider whitespace-nowrap';
export const thR = 'px-3 py-2 text-right text-[11px] font-medium text-slate-400 uppercase tracking-wider whitespace-nowrap';
export const tdCls = 'px-3 py-1.5 text-slate-200 whitespace-nowrap';
export const tdR = 'px-3 py-1.5 text-right tabular-nums text-slate-200 whitespace-nowrap';
export const tableWrap = 'rounded-xl border border-slate-700/40 bg-slate-900/30 overflow-auto';
export const controlsCls = 'flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30';
export const labelCls = 'text-xs font-medium text-slate-400 uppercase tracking-wider';
